import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

// The time lane reserves 160px; the header yields first when the host cannot fit both minima.
const HEADER_MIN = 112;
const HEADER_MAX = 320;
const TIME_LANE_MIN = 160;
const END_PADDING = 2;

export function useTrackHeaderResize({
  visible,
  resizable,
  width,
  defaultWidth,
  minWidth,
  maxWidth,
  onWidthChange,
}: {
  visible: boolean;
  resizable: boolean;
  width?: number;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [hostWidth, setHostWidth] = useState<number | null>(null);
  const requestedMin =
    minWidth !== undefined && Number.isFinite(minWidth)
      ? Math.max(0, Math.round(minWidth))
      : HEADER_MIN;
  const requestedMax = Math.max(
    requestedMin,
    maxWidth !== undefined && Number.isFinite(maxWidth)
      ? Math.max(0, Math.round(maxWidth))
      : HEADER_MAX,
  );
  const [localWidth, setLocalWidth] = useState(defaultWidth ?? requestedMin);
  const active =
    visible &&
    (resizable ||
      width !== undefined ||
      defaultWidth !== undefined ||
      minWidth !== undefined ||
      maxWidth !== undefined);
  const max = Math.min(
    requestedMax,
    Math.max(0, Math.floor((hostWidth ?? Infinity) - TIME_LANE_MIN - END_PADDING)),
  );
  const min = Math.min(requestedMin, max);
  const clamp = (value: number) =>
    Math.round(Math.max(min, Math.min(max, Number.isFinite(value) ? value : requestedMin)));
  const resolvedWidth = clamp(width ?? localWidth);
  const drag = useRef<{
    id: number;
    x: number;
    width: number;
    direction: number;
    target: HTMLDivElement;
  } | null>(null);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!active || !viewport) return;
    const measure = () => setHostWidth(viewport.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [active]);

  const release = () => {
    const current = drag.current;
    drag.current = null;
    if (current?.target.hasPointerCapture(current.id))
      current.target.releasePointerCapture(current.id);
  };
  useLayoutEffect(() => release, [active, resizable]);

  const requestWidth = (value: number) => {
    const next = clamp(value);
    if (width === undefined) setLocalWidth(next);
    if (next !== resolvedWidth) onWidthChange?.(next);
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || drag.current) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.focus({ preventScroll: true });
    drag.current = {
      id: event.pointerId,
      x: event.clientX,
      width: resolvedWidth,
      direction: getComputedStyle(event.currentTarget).direction === "rtl" ? -1 : 1,
      target: event.currentTarget,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    requestWidth(current.width + (event.clientX - current.x) * current.direction);
  };
  const finish = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerId === drag.current?.id) release();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && drag.current) {
      event.preventDefault();
      event.stopPropagation();
      requestWidth(drag.current.width);
      release();
      return;
    }
    // Space belongs to this layout control, never to the global playback shortcut.
    if (event.key === " ") {
      event.preventDefault();
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const direction = getComputedStyle(event.currentTarget).direction === "rtl" ? -1 : 1;
    const step = event.shiftKey ? 32 : 8;
    const next =
      event.key === "Home"
        ? min
        : event.key === "End"
          ? max
          : event.key === "ArrowLeft"
            ? resolvedWidth - step * direction
            : event.key === "ArrowRight"
              ? resolvedWidth + step * direction
              : undefined;
    if (next === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    requestWidth(next);
  };
  return {
    viewportRef,
    style: active
      ? ({ "--c6-media-track-label-gutter": `${resolvedWidth}px` } as CSSProperties)
      : undefined,
    separatorProps: {
      "aria-valuemin": min,
      "aria-valuemax": max,
      "aria-valuenow": resolvedWidth,
      "aria-valuetext": `${resolvedWidth}px`,
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: finish,
      onLostPointerCapture: finish,
      onKeyDown,
    },
  };
}
